import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Step } from "react-joyride";
import type { DashboardVariant } from "@/lib/dashboardVariant";

// TES-1701: anchor IDs the dashboard tour can target. The full ordered catalogue
// lives in ALL_STEPS below; per-variant we render ONLY the steps whose anchor is
// actually mounted on that variant's dashboard — react-joyride@3.1.0 otherwise
// silently skips a step with a missing target (addFailure 'target_not_found' →
// index+1, no tooltip), which the owner saw as „man sieht teilweise die Label nicht".
type DashboardTourAnchor =
  | "dashboard-filters"
  | "dashboard-inspections"
  | "dashboard-defects"
  | "dashboard-charts"
  | "dashboard-defect-charts"
  | "dashboard-team";

/** Variant whose dashboard actually renders `dashboard-*` anchors (excludes admin-/qm-admin-redirect). */
export type DashboardTourVariant = Exclude<
  DashboardVariant,
  "admin-redirect" | "qm-admin-redirect"
>;

// Which anchors each '/'-dashboard variant (HomeRoute → VARIANT_VIEWS) actually
// MOUNTS. Source of truth, derived from the dashboard components:
//  - qualitaetsmanager → Dashboard.tsx with canSeeAll=true, showInspections=true → all 6.
//  - generic (customer_approver) → Dashboard.tsx with canSeeAll=false, showInspections=true
//    → all except dashboard-team (TeamStatistics is QM/admin-only).
//  - tester → TesterDashboard → dashboard-inspections + dashboard-defects (RoleTileSection).
//  - mangelbearbeiter → MangelbearbeiterDashboard → dashboard-defects only (no inspections).
//  - super-admin → SuperAdminDashboard → NO dashboard-* anchors → no tour.
// admin-redirect / qm-admin-redirect never render a dashboard (HomeRoute redirects to
// /admin/users resp. /tasks, TES-2101), so neither has an entry here.
// Module-internal (NOT exported): the variant→anchor mapping is an implementation
// detail of the hook. Behaviour is verified through the public hook output in the
// unit tests (avoids a test-only "dead export" — fallow ignores __tests__ consumers).
const DASHBOARD_TOUR_ANCHORS: Record<DashboardTourVariant, readonly DashboardTourAnchor[]> = {
  qualitaetsmanager: [
    "dashboard-filters",
    "dashboard-inspections",
    "dashboard-defects",
    "dashboard-charts",
    "dashboard-defect-charts",
    "dashboard-team",
  ],
  generic: [
    "dashboard-filters",
    "dashboard-inspections",
    "dashboard-defects",
    "dashboard-charts",
    "dashboard-defect-charts",
  ],
  tester: ["dashboard-inspections", "dashboard-defects"],
  mangelbearbeiter: ["dashboard-defects"],
  "super-admin": [],
};

/**
 * Variant-aware dashboard tour steps (TES-1701).
 *
 * `variant` selects which steps are emitted — ONLY steps whose anchor is mounted
 * on that variant's dashboard, so no step is ever silently skipped. When omitted
 * it defaults to the full `qualitaetsmanager` set (the legacy behaviour the
 * unit-style callers rely on). A variant with no anchors (super-admin /
 * admin-redirect → null) yields an empty array → DashboardTour renders nothing.
 */
export function useDashboardTourSteps(
  variant: DashboardTourVariant | null = "qualitaetsmanager",
): Step[] {
  const { t } = useTranslation();

  return useMemo(() => {
    // Full ordered catalogue keyed by anchor — the per-step scroll config (TES-1659)
    // is preserved verbatim; only the SELECTION is variant-aware now.
    const ALL_STEPS: Record<DashboardTourAnchor, Step> = {
      "dashboard-filters": {
        target: '[data-tour="dashboard-filters"]',
        title: t("tour.dashboardFilters.title"),
        content: t("tour.dashboardFilters.content"),
        placement: "bottom" as const,
        // TES-1701: skipBeacon is applied uniformly to the ENTRY step below, so the
        // tour auto-opens for every variant — not just the one starting on filters.
        // TES-1659: clear the sticky header so the spotlight isn't hidden under it.
        scrollOffset: 120,
        scrollDuration: 400,
      },
      "dashboard-inspections": {
        // TES-1659: these targets wrap a FULL section. With "bottom"/"auto"
        // placement react-joyride's scroll engine (useScrollEffect) only brings
        // the target TOP to scrollOffset and does NOT compensate for the tooltip
        // rendered below it → the tooltip clipped ~1280px off-screen. Only the
        // "top" branch of adjustForPlacement subtracts the floater height, so we
        // place the tooltip ABOVE the section (with top-step headroom) — that is
        // the placement the engine reliably scrolls fully into view.
        target: '[data-tour="dashboard-inspections"]',
        title: t("tour.dashboardInspections.title"),
        content: t("tour.dashboardInspections.content"),
        placement: "top" as const,
        scrollOffset: 260,
        scrollDuration: 400,
      },
      "dashboard-defects": {
        target: '[data-tour="dashboard-defects"]',
        title: t("tour.dashboardDefects.title"),
        content: t("tour.dashboardDefects.content"),
        placement: "top" as const,
        scrollOffset: 260,
        scrollDuration: 400,
      },
      "dashboard-charts": {
        // TES-1659: top-placement steps need extra headroom so the tooltip
        // (rendered ABOVE the target) plus the spotlight both fit in the viewport.
        target: '[data-tour="dashboard-charts"]',
        title: t("tour.dashboardCharts.title"),
        content: t("tour.dashboardCharts.content"),
        placement: "top" as const,
        scrollOffset: 260,
        scrollDuration: 400,
      },
      "dashboard-defect-charts": {
        target: '[data-tour="dashboard-defect-charts"]',
        title: t("tour.dashboardDefectCharts.title"),
        content: t("tour.dashboardDefectCharts.content"),
        placement: "top" as const,
        scrollOffset: 260,
        scrollDuration: 400,
      },
      "dashboard-team": {
        target: '[data-tour="dashboard-team"]',
        title: t("tour.dashboardTeam.title"),
        content: t("tour.dashboardTeam.content"),
        placement: "top" as const,
        scrollOffset: 260,
        scrollDuration: 400,
      },
    };

    const anchors = variant ? DASHBOARD_TOUR_ANCHORS[variant] : [];
    return anchors.map((anchor, index) => {
      const step = ALL_STEPS[anchor];
      // TES-1701: the ENTRY step must skip the beacon so the tour opens its tooltip
      // immediately (instead of waiting for a beacon click). For QM that's already
      // `dashboard-filters`; for variants whose tour starts elsewhere (tester →
      // dashboard-inspections, mangelbearbeiter → dashboard-defects) we set it on
      // whatever ends up first, so every variant's tour auto-opens identically.
      return index === 0 ? { ...step, skipBeacon: true } : step;
    });
  }, [t, variant]);
}
